import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PropertyCard from "./PropertyCard";
import SavedSearchesBar from "../components/SavedSearchesBar";
import AuthGate from "../components/AuthGate";
import { PropertyGridSkeleton, PropertyCardSkeleton } from "../components/Skeletons";
import AccessHouseEntryCTA from "../components/AccessHouseEntryCTA";
import HeroSearch from "../components/HeroSearch";
import FilterPills from "../components/FilterPills";
import ActiveFilterChips from "../components/ActiveFilterChips";
import { Sparkles, ArrowRight, Crown, Lock, TrophyIcon, Trophy, Pencil, Building2, ArrowUp, ArrowDown } from "lucide-react";
import TestimonialsSection from "./TestimonialsSection";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { appendCustomFilterParams } from "../lib/filterUtils";
import { filtersToQuery, queryToFilters, queryToPage } from "../lib/filterUrl";
import { Button } from "../components/ui/button";
// v2.71.26 — Scalable property module: paginated list + IntersectionObserver
// infinite scroll. Replaces the legacy `/api/properties` (up to 2000 docs in
// one shot) with `/api/properties/search` (24 per page, indexed Mongo query).
import { useProperties } from "../features/property/hooks";

const defaultFilters = () => ({
  priceRange: [500000, 300000000], main_area: "", sub_area: "", area: "", bhk: "", listing_type: "", property_type: "", maxMetro: 3000, tag: "",
  // v2.70 Phase 1 — top-level Buy/Rent/Lease toggle. Empty = show all.
  listing_intent: "",
  // v2.85 Phase B — Posted By (Owner / Dealer / Builder). Empty = all.
  posted_by: "",
  // v2.87 Phase 1 — Filter rebuild keys aligned with v2.86 listing form.
  furnishing: "",
  possession_status: "",
  min_size: "",
  max_size: "",
  verified: false,
  sort: "",  // Empty = backend default (created_at:desc)
});

// v2.87 Phase 3a — Build the active-filter chips list rendered above
// All Listings. Each non-default filter explodes into 1+ chips (multi-
// select fields like property_type / bhk / furnishing split on comma).
// `onRemove` patches the parent filter state to clear that single
// entry, leaving the rest intact.
const buildActiveChips = (filters, configFilters, setFilters, defaults) => {
  const chips = [];
  const intentLabel = { sale: "Sale", rent: "Rent", lease: "Lease" };
  if (filters.listing_intent) {
    chips.push({
      key: "listing_intent",
      label: intentLabel[filters.listing_intent] || filters.listing_intent,
      onRemove: () => setFilters((p) => ({ ...p, listing_intent: "" })),
    });
  }
  if (filters.listing_type) {
    chips.push({
      key: "listing_type",
      label: filters.listing_type,
      onRemove: () => setFilters((p) => ({ ...p, listing_type: "" })),
    });
  }
  const _pushCsv = (key, csv, onSet) => {
    const parts = String(csv || "").split(",").map((x) => x.trim()).filter(Boolean);
    parts.forEach((val) => {
      chips.push({
        key, value: val, label: val,
        onRemove: () => {
          const next = parts.filter((x) => x !== val).join(",");
          onSet(next);
        },
      });
    });
  };
  _pushCsv("property_type", filters.property_type,
    (next) => setFilters((p) => ({ ...p, property_type: next })));
  _pushCsv("bhk", filters.bhk,
    (next) => setFilters((p) => ({ ...p, bhk: next })));
  _pushCsv("furnishing", filters.furnishing,
    (next) => setFilters((p) => ({ ...p, furnishing: next })));
  // v2.87 Phase 3b ext — Main Area is now multi-select; explode into chips.
  // Removing any main_area chip ALWAYS clears sub_area, since sub_area
  // is only valid when a single main_area is picked (and the UI gates
  // entry to it on that invariant).
  _pushCsv("main_area", filters.main_area,
    (next) => setFilters((p) => ({ ...p, main_area: next, sub_area: "" })));
  if (filters.sub_area) {
    chips.push({
      key: "sub_area",
      label: filters.sub_area,
      onRemove: () => setFilters((p) => ({ ...p, sub_area: "" })),
    });
  }
  if (filters.possession_status) {
    const possLabels = { ready_to_move: "Ready to Move", under_construction: "Under Construction", new_launch: "New Launch" };
    chips.push({
      key: "possession_status",
      label: possLabels[filters.possession_status] || filters.possession_status,
      onRemove: () => setFilters((p) => ({ ...p, possession_status: "" })),
    });
  }
  // v2.87 Phase 3b ext — Tag is now multi-select; explode into chips.
  _pushCsv("tag", filters.tag,
    (next) => setFilters((p) => ({ ...p, tag: next })));
  if (filters.posted_by) {
    chips.push({
      key: "posted_by",
      label: `Posted by ${filters.posted_by}`,
      onRemove: () => setFilters((p) => ({ ...p, posted_by: "" })),
    });
  }
  if (filters.verified) {
    chips.push({
      key: "verified", label: "Verified",
      onRemove: () => setFilters((p) => ({ ...p, verified: false })),
    });
  }
  // Price range — only show chip when not default.
  const pr = filters.priceRange;
  const dpr = defaults?.priceRange || [500000, 300000000];
  if (pr && (pr[0] !== dpr[0] || pr[1] !== dpr[1])) {
    const fmt = (n) => n >= 10000000 ? `₹${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)} Cr` :
                      n >= 100000 ? `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} L` :
                      n >= 1000 ? `₹${(n / 1000).toFixed(0)}K` : `₹${n}`;
    const intent = filters.listing_intent || "sale";
    const lbl = intent === "rent" ? "Rent" : intent === "lease" ? "Lease" : "Price";
    chips.push({
      key: "price",
      label: pr[0] === 0 ? `${lbl}: up to ${fmt(pr[1])}` : `${lbl}: ${fmt(pr[0])} – ${fmt(pr[1])}`,
      onRemove: () => setFilters((p) => ({ ...p, priceRange: dpr })),
    });
  }
  // Size — top-level Min / Max.
  if (filters.min_size || filters.max_size) {
    const part = (filters.min_size && filters.max_size) ? `${filters.min_size}–${filters.max_size}`
                : filters.min_size ? `≥ ${filters.min_size}` : `≤ ${filters.max_size}`;
    chips.push({
      key: "size", label: `Size ${part}`,
      onRemove: () => setFilters((p) => ({ ...p, min_size: "", max_size: "" })),
    });
  }
  // Metro distance — only if non-default.
  const defMetro = defaults?.maxMetro ?? 3000;
  if (filters.maxMetro && filters.maxMetro !== defMetro) {
    chips.push({
      key: "metro", label: `Metro ≤ ${filters.maxMetro}m`,
      onRemove: () => setFilters((p) => ({ ...p, maxMetro: defMetro })),
    });
  }
  if (filters.sort) {
    const sortLabels = { "created_at:desc": "Newest", "price:asc": "Price ↑", "price:desc": "Price ↓", "distance_metro_m:asc": "Closest metro" };
    chips.push({
      key: "sort", label: `Sort: ${sortLabels[filters.sort] || filters.sort}`,
      onRemove: () => setFilters((p) => ({ ...p, sort: "" })),
    });
  }
  // Dynamic custom filters (admin-defined).
  for (const f of configFilters || []) {
    if (!f.key || ["price", "bhk", "property_type", "listing_type", "main_area", "sub_area", "metro_distance", "size", "tag", "furnishing"].includes(f.key)) continue;
    const v = filters[f.key];
    if (v === undefined || v === "" || v === null || v === false) continue;
    if (Array.isArray(v)) continue;
    chips.push({
      key: f.key, label: `${f.name || f.key}: ${v}`,
      onRemove: () => setFilters((p) => ({ ...p, [f.key]: "" })),
    });
  }
  return chips;
};

// Count how many filters diverge from defaults — used for the mobile filter trigger badge.
const countActiveFilters = (f) => {
  const d = defaultFilters();
  let n = 0;
  if (f.listing_type && f.listing_type !== d.listing_type) n++;
  if (f.listing_intent && f.listing_intent !== d.listing_intent) n++;
  if (f.main_area && f.main_area !== d.main_area) n++;
  if (f.sub_area && f.sub_area !== d.sub_area) n++;
  if (f.area && f.area !== d.area) n++;
  if (f.bhk && f.bhk !== d.bhk) n++;
  if (f.property_type && f.property_type !== d.property_type) n++;
  if (f.tag && f.tag !== d.tag) n++;
  if (f.posted_by && f.posted_by !== d.posted_by) n++;
  if (f.priceRange && (f.priceRange[0] !== d.priceRange[0] || f.priceRange[1] !== d.priceRange[1])) n++;
  if (f.maxMetro !== d.maxMetro) n++;
  // v2.87 Phase 1 — new filter keys.
  if (f.furnishing) n++;
  if (f.possession_status) n++;
  if (f.min_size) n++;
  if (f.max_size) n++;
  if (f.verified) n++;
  if (f.sort) n++;
  return n;
};

export default function Dashboard() {
  const loc = useLocation();
  const nav = useNavigate();
  const [topPicks, setTopPicks] = useState({ results: [], locked: false });
  const [premiumDeals, setPremiumDeals] = useState({ results: [], locked: false });
  const [success, setSuccess] = useState([]);
  // v2.71.6 Phase 6 — Hydrate initial filter state from URL query string so
  // shared / bookmarked search links restore the same view.
  const [filters, setFilters] = useState(() => queryToFilters(window.location.search, defaultFilters()));
  const [content, setContent] = useState(null);
  const [showSavedGate, setShowSavedGate] = useState(false);  // v2.71.6 — anon save prompt
  const skipNextUrlSync = useRef(false);
  const { user, tier, presentationMode, isAdmin, isDealer, isBuilder } = useAuth();
  // v2.67 — Admin-defined custom filters (parking, rent_lease, furnishing, …)
  // must be forwarded to /api/properties/search or the backend can't filter them.
  // ConfigContext already hydrates these via /api/filters.
  const { filters: configFilters = [] } = useConfig();
  const isDealerOrBuilder = isDealer || isBuilder;
  const sv = content?.section_visibility || {};
  const showHero = sv.hero !== false;
  const showFilter = sv.filter !== false;
  const showPremiumDeals = sv.premium_deals !== false;
  const showTopPicks = sv.top_picks !== false;
  const showSuccess = sv.success_stories !== false;
  const showTestimonials = sv.testimonials !== false;
  const showProjectsCta = sv.projects !== false;

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  // v2.71.68 — Mobile bottom-nav "Search" tab routes here as `/?focus=search`.
  // Previously this query had no handler, so tapping Search did nothing —
  // the broken mobile search trigger the spec calls out. Now we focus the
  // hero-search input on mount and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("focus") === "search") {
      setTimeout(() => {
        const input = document.querySelector('[data-testid="hs-input"]')
          || document.querySelector('[data-testid="hero-search-input"]')
          || document.querySelector('input[type="text"]');
        if (input) {
          input.scrollIntoView({ block: "center", behavior: "smooth" });
          input.focus({ preventScroll: true });
        }
        const url = new URL(window.location.href);
        url.searchParams.delete("focus");
        window.history.replaceState({}, "", url.toString());
      }, 80);
    }
  }, [loc.search]);

  // v2.71.26 — Map Dashboard's bespoke filter object → flat params for the
  // /properties/search endpoint. Memoised so the hook's deep-key compare
  // sees a stable reference until a filter actually changes.
  const searchFilters = useMemo(() => {
    const p = {};
    if (filters.main_area) p.main_area = filters.main_area;
    if (filters.sub_area) p.sub_area = filters.sub_area;
    if (filters.area && !filters.main_area) p.area = filters.area;
    if (filters.bhk) p.bhk = filters.bhk;
    if (filters.listing_type) p.listing_type = filters.listing_type;
    if (filters.listing_intent) p.listing_intent = filters.listing_intent;
    if (filters.property_type) p.property_type = filters.property_type;
    if (filters.priceRange?.[0] > 1000000)
      p.min_price = Math.max(p.min_price || 0, filters.priceRange[0]);
    if (filters.priceRange?.[1] < 30000000)
      p.max_price = filters.priceRange[1];
    if (filters.maxMetro < 3000) p.max_metro_distance = filters.maxMetro;
    if (filters.tag) p.tag = filters.tag;
    // v2.85 Phase B — Posted-By filter forwarded to /api/properties/search.
    if (filters.posted_by) p.posted_by = filters.posted_by;
    // v2.87 Phase 1 — New filter keys aligned with v2.86 listing form.
    if (filters.furnishing) p.furnishing = filters.furnishing;
    if (filters.possession_status) p.possession_status = filters.possession_status;
    if (filters.min_size) p.min_size = filters.min_size;
    if (filters.max_size) p.max_size = filters.max_size;
    if (filters.verified) p.verified = true;
    appendCustomFilterParams(p, filters, configFilters);
    return p;
  }, [filters, configFilters]);

  // v2.87 Phase 1 — Sort param: defaults to "created_at:desc" when filter
  // not set. Allowed sort fields enforced server-side by SortParams.
  const currentSort = filters.sort || "created_at:desc";

  // v2.71.26 — Paginated property list with auto infinite-scroll sentinel.
  // v2.75 — initialPage hydrated from `?page=N` so browser Back / Refresh
  // reconstructs the user's prior browsing depth before scroll restoration
  // attempts to anchor onto the previously-clicked card.
  const initialPage = useMemo(() => queryToPage(window.location.search), []);
  const {
    items: properties,
    total: propertiesTotal,
    isLoading: propertiesLoading,
    isLoadingMore: propertiesLoadingMore,
    hasMore: propertiesHasMore,
    page: propertiesPage,
    sentinelRef,
  } = useProperties({
    filters: searchFilters,
    pageSize: 24,
    sort: currentSort,
    initialPage,
  });

  // Curated sections (top-picks / premium-deals / success-stories) are
  // separate from the main paginated list — they're tier-gated server-side
  // curated collections, not "all approved properties". Refetched only when
  // viewer (user) changes — the curation logic depends on role/tier.
  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/properties/top-picks"),
      api.get("/properties/premium-deals"),
      api.get("/properties/success-stories"),
    ]).then(([topRes, premRes, sucRes]) => {
      if (cancel) return;
      setTopPicks(topRes.data);
      setPremiumDeals(premRes.data);
      setSuccess(sucRes.data);
    });
    return () => { cancel = true; };
  }, [user?.id]);

  // v2.71.75 — Map gating is tier-aware again:
  //   - tier=null (free preview) → "Unlock Map" upsell pill
  //   - tier="normal" or higher (any paid plan) → "View on map" link
  // Previously prime/premium-only; normal added so entry-level paid tier
  // (₹999/yr) sees the map link consistent with the lock-screen rule.
  const propertiesMeta = useMemo(() => {
    const isPaidTier = !!tier && tier !== "free" && tier !== "basic";
    return {
      total: propertiesTotal,
      tier,
      limit: null,
      limited: false,
      features: { map: isPaidTier },
    };
  }, [propertiesTotal, tier]);
  // Aliases to keep the existing JSX legible without a global rename.
  const loading = propertiesLoading;
  const meta = propertiesMeta;

  // v2.71.6 Phase 6 — Persist current filters in URL. Uses replace() so each
  // change adds ONE history entry (so browser back undoes filter steps cleanly).
  // v2.75 — Also persists `?page=N` so deep-scroll → property → Back restores
  // the same N pages worth of cards (single oversized fetch, see useProperties).
  useEffect(() => {
    if (skipNextUrlSync.current) { skipNextUrlSync.current = false; return; }
    const qs = filtersToQuery(filters, defaultFilters(), { page: propertiesPage });
    const target = `${loc.pathname}${qs ? `?${qs}` : ""}`;
    const current = `${loc.pathname}${loc.search}`;
    if (target !== current) nav(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, propertiesPage]);

  // v2.71.6 Phase 6 — React to back/forward navigation OR programmatic URL
  // changes (e.g. clicking a Saved Search chip elsewhere): re-hydrate filter
  // state from the URL so the view + URL stay in sync.
  useEffect(() => {
    const fromUrl = queryToFilters(loc.search, defaultFilters());
    // Cheap deep-equal to avoid feedback loops
    const same = JSON.stringify(fromUrl) === JSON.stringify(filters);
    if (!same) {
      skipNextUrlSync.current = true;
      setFilters(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {!presentationMode && showHero && (
        <section className="hero-backdrop border-b border-[#E4E4E7] relative" data-testid="hero-section">
          {isAdmin && (
            <Link
              to="/admin?tab=content"
              className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white/95 hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#C7D9FF] px-2.5 py-1 rounded-sm shadow-sm transition-colors z-10"
              data-testid="edit-hero-btn"
            >
              <Pencil size={11} /> Edit Hero
            </Link>
          )}
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF] mb-4" data-testid="hero-eyebrow">
                  {content?.hero_eyebrow || "◉ HouseEntry Solutions · Delhi West"}
                </div>
                <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#09090B] leading-[1.05]" data-testid="hero-headline">
                  {content?.hero_headline || "Close smarter deals"}
                  <br />
                  <span className="text-[#0055FF]">{content?.hero_headline_accent || "in Uttam Nagar, Nawada"}</span>
                  <br />
                  <span className="text-[#A1A1AA]">{content?.hero_headline_tail || "& Dwarka Mor."}</span>
                </h1>
                <p className="mt-5 text-[15px] text-[#52525B] max-w-xl leading-relaxed">
                  {content?.hero_subline || "A controlled marketplace — admin-curated listings, dealer KYC, centralized enquiries."}
                  <br />
                  <span className="text-[#0055FF] font-semibold">{content?.hero_tagline || "Exclusive listings. Verified deals. Smarter choices."}</span>
                </p>
                {!user && (
                  <div className="mt-6">
                    <AccessHouseEntryCTA variant="primary" />
                  </div>
                )}
                {/* v2.87 Phase 1 — Hero intent toggle REMOVED.
                    Intent chips (All / Sale / Rent / Lease) now live inside
                    <FilterPills> right above the listings grid so the user
                    can switch intent next to results — no more scrolling
                    past the hero + Best Deals to see the effect. */}
              </div>
              <div className="lg:col-span-4 grid grid-cols-3 gap-2">
                <Stat label="Listings" value={meta.total} />
                <Stat label="Avg ROI" value={`${avg(properties, "roi").toFixed(1)}%`} accent />
                <Stat label="Sold 15d" value={success.length} />
              </div>
            </div>

            {/* v2.71.60 — Single premium global search bar (replaces the
                v2.71.58 3-field capsule). Self-navigates to property
                detail on result tap. Listings filtering continues via
                <FilterPills> below. */}
            <div className="mt-10 lg:mt-12">
              <HeroSearch />
            </div>
          </div>
        </section>
      )}

      {!presentationMode && meta.limited && !isDealerOrBuilder && (
        <div className="bg-[#0055FF] text-white" data-testid="tier-limit-banner">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="text-[13px] flex items-center gap-2">
              <Lock size={14} />
              {tier
                ? <>You're on <b className="font-semibold">{meta.tier}</b> plan — viewing {meta.limit} of {meta.total} listings.</>
                : <>Aap preview mode me hain — <b>{meta.limit}</b> of <b>{meta.total}</b> listings dikhayi de rahi hain. Continue karne ke liye login karein ya plan choose karein.</>}
            </div>
            <div className="flex items-center gap-2">
              {!user && (
                <Link to="/login" data-testid="banner-login-btn">
                  <Button size="sm" variant="outline" className="bg-white text-[#0055FF] hover:bg-[#FAFAFA] rounded-sm border-white">
                    Login
                  </Button>
                </Link>
              )}
              <Link to="/pricing" data-testid="banner-upgrade-btn">
                <Button size="sm" variant="outline" className="bg-white text-[#0055FF] hover:bg-[#FAFAFA] rounded-sm border-white">
                  <Crown size={13} className="mr-1.5" /> {user ? "Upgrade" : "View Plans"}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {/* v2.87 Phase 3a — Mobile sticky filter bar at TOP of dashboard
            REMOVED per user spec: "Full listing filter bar should appear
            just above All Listings, not above Premium/Best Deals." The
            in-listings <FilterPills> wrapper below renders for BOTH mobile
            and desktop now. */}
        {renderOrderedSections({
          content, isAdmin, presentationMode, isDealerOrBuilder,
          showPremiumDeals, premiumDeals,
          showTopPicks, topPicks, tier,
          showFilter, filters, setFilters, defaultFilters,
          configFilters,
          loading, properties, meta,
          // v2.71.26 — Paginated listings hook plumbed through to renderer.
          propertiesLoadingMore, propertiesHasMore, sentinelRef,
          showSuccess, success,
          showProjectsCta,
          showTestimonials,
          setShowSavedGate,  // v2.71.6 Phase 6 — anon save-search prompt
          moveSection: async (key, dir) => {
            try {
              const order = [...(content?.section_order || DEFAULT_SECTION_ORDER_FE)];
              const i = order.indexOf(key);
              if (i < 0) return;
              const j = i + dir;
              if (j < 0 || j >= order.length) return;
              [order[i], order[j]] = [order[j], order[i]];
              const r = await api.put("/admin/content", { section_order: order });
              setContent(r.data);
            } catch (_) { /* silent */ }
          },
        })}
      </div>
      {/* v2.71.6 Phase 6 — Soft prompt when anonymous visitor tries to save a search */}
      <AuthGate
        open={showSavedGate}
        onClose={() => setShowSavedGate(false)}
        action="save"
        returnTo={`${loc.pathname}${loc.search}`}
      />
    </div>
  );
}

const DEFAULT_SECTION_ORDER_FE = [
  "premium_deals", "top_picks", "listings", "success_stories", "projects", "testimonials",
];

function AdminOrderControls({ sectionKey, onMove }) {
  return (
    <div className="absolute -top-2 left-0 inline-flex items-center gap-1 bg-white border border-[#C7D9FF] rounded-sm px-1 py-0.5 shadow-sm" data-testid={`section-reorder-${sectionKey}`}>
      <button
        type="button"
        onClick={() => onMove(sectionKey, -1)}
        className="p-1 text-[#0055FF] hover:bg-[#EEF4FF] rounded-sm"
        title="Move up"
        data-testid={`section-up-btn-${sectionKey}`}
      >
        <ArrowUp size={11} />
      </button>
      <button
        type="button"
        onClick={() => onMove(sectionKey, 1)}
        className="p-1 text-[#0055FF] hover:bg-[#EEF4FF] rounded-sm"
        title="Move down"
        data-testid={`section-down-btn-${sectionKey}`}
      >
        <ArrowDown size={11} />
      </button>
    </div>
  );
}

function renderOrderedSections({
  content, isAdmin, presentationMode, isDealerOrBuilder,
  showPremiumDeals, premiumDeals,
  showTopPicks, topPicks, tier,
  showFilter, filters, setFilters, defaultFilters,
  configFilters,
  loading, properties, meta,
  // v2.71.26 — paginated listings extras passed through from Dashboard.
  propertiesLoadingMore, propertiesHasMore, sentinelRef,
  showSuccess, success,
  showProjectsCta,
  showTestimonials,
  setShowSavedGate,
  moveSection,
}) {
  const order = (content?.section_order && content.section_order.length)
    ? content.section_order
    : DEFAULT_SECTION_ORDER_FE;

  const sectionsMap = {
    premium_deals: (!presentationMode && showPremiumDeals && premiumDeals.results.length > 0) ? (
      <section key="premium_deals" className="mb-10 relative" data-testid="premium-deals-section">
        {isAdmin && <AdminOrderControls sectionKey="premium_deals" onMove={moveSection} />}
        {isAdmin && (
          <Link to="/admin?tab=content" className="absolute -top-2 right-0 text-[10px] font-semibold text-[#0055FF] hover:underline inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded-sm border border-[#C7D9FF]" data-testid="edit-premium-deals">
            <Pencil size={10} /> Edit
          </Link>
        )}
        <div className="flex items-end justify-between mb-5 pb-3 border-b border-[#E4E4E7]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF] mb-1 flex items-center gap-1.5">
              <Crown size={12} /> Premium Exclusive
            </div>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-[#09090B]">
              Premium Deals — visible only to you
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {premiumDeals.results.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      </section>
    ) : null,

    top_picks: (!presentationMode && showTopPicks) ? (
      <section key="top_picks" className="mb-10 relative" data-testid="top-picks-section">
        {isAdmin && <AdminOrderControls sectionKey="top_picks" onMove={moveSection} />}
        {isAdmin && (
          <Link to="/admin?tab=content" className="absolute -top-2 right-0 text-[10px] font-semibold text-[#0055FF] hover:underline inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded-sm border border-[#C7D9FF]" data-testid="edit-top-picks">
            <Pencil size={10} /> Edit
          </Link>
        )}
        <div className="flex items-end justify-between mb-5 pb-3 border-b border-[#E4E4E7]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#E53935] mb-1 flex items-center gap-1.5">
              <Sparkles size={12} /> Curated by data
            </div>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-[#09090B]">
              Best Deals — highest ROI right now
            </h2>
          </div>
        </div>
        {topPicks.locked ? (
          isDealerOrBuilder ? null : (
            <LockedSection title="Best Deals is a Prime feature" desc="Get ROI-ranked best deals curated for you with the Prime plan." cta={tier ? "Upgrade to Prime" : "Get Prime"} />
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {topPicks.results.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
          </div>
        )}
      </section>
    ) : null,

    listings: (
      <div key="listings" className="relative" data-testid="listings-section">
        {isAdmin && <AdminOrderControls sectionKey="listings" onMove={moveSection} />}
        <main>
          {/* v2.71.6 Phase 6 — Saved Searches bar (desktop only; mobile uses sheet) */}
          <div className="hidden md:block">
            <SavedSearchesBar
              filters={filters}
              setFilters={setFilters}
              defaults={defaultFilters()}
              onRequireLogin={() => setShowSavedGate?.(true)}
            />
          </div>

          {/* v2.87 Phase 3a — In-listings sticky filter strip. Renders for
              BOTH mobile and desktop (was desktop-only pre-3a). Lives just
              above All Listings per user spec: "Full listing filter bar
              should appear just above All Listings, not above Premium/
              Best Deals." */}
          {!presentationMode && showFilter && (
            <div
              className="sticky top-[48px] sm:top-[52px] lg:top-[56px] z-20 -mx-3 px-3 sm:mx-0 sm:px-0 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-slate-100 mb-4"
              data-results-anchor="filter-strip"
            >
              <FilterPills
                filters={filters}
                setFilters={setFilters}
                onReset={() => setFilters(defaultFilters())}
                configFilters={configFilters}
                defaults={defaultFilters()}
              />
            </div>
          )}

          {/* v2.87 Phase 3a — Active filter chips strip. Each non-default
              filter is rendered as a removable chip. "Clear all" link on
              the right. */}
          {!presentationMode && (
            <ActiveFilterChips
              chips={buildActiveChips(filters, configFilters, setFilters, defaultFilters())}
              onClearAll={() => setFilters(defaultFilters())}
              testidPrefix="afc"
            />
          )}

          <div className="flex items-end justify-between mb-5 pb-3 border-b border-[#E4E4E7]">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-1">All Listings</div>
              <h2 className="font-heading font-semibold text-2xl tracking-tight text-[#09090B]">
                <span data-testid="result-count">{properties.length}</span>
                {meta.total > properties.length && <span className="text-[#A1A1AA]"> of {meta.total}</span>} properties
              </h2>
            </div>
            {meta.features?.map ? (
              <Link to="/map" className="text-[12px] font-semibold text-[#0055FF] hover:underline inline-flex items-center gap-1" data-testid="view-map-link">
                View on map <ArrowRight size={12} />
              </Link>
            ) : (
              <Link to="/pricing" className="text-[12px] font-semibold text-[#A1A1AA] hover:text-[#0055FF] inline-flex items-center gap-1" data-testid="map-locked-link">
                <Lock size={11} /> Unlock Map
              </Link>
            )}
          </div>
          {loading ? (
            <PropertyGridSkeleton count={6} />
          ) : properties.length === 0 ? (
            <div className="text-center py-20 text-[#A1A1AA] border border-dashed border-[#E4E4E7] rounded-sm" data-testid="empty-state">
              No properties match your filters.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {properties.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
                {/* v2.71.26 — Skeleton placeholders while next page loads, so the
                    user sees a hint of upcoming content instead of a sudden
                    layout shift / dead bottom edge. */}
                {propertiesLoadingMore && Array.from({ length: 3 }).map((_, i) => (
                  <PropertyCardSkeleton key={`loading-more-${i}`} />
                ))}
              </div>
              {/* IntersectionObserver sentinel: when this enters the viewport
                  (200 px before the actual bottom), the hook auto-fires the
                  next page. Kept invisible to users. */}
              {propertiesHasMore && (
                <div
                  ref={sentinelRef}
                  aria-hidden="true"
                  className="h-2 w-full"
                  data-testid="properties-infinite-sentinel"
                />
              )}
              {!propertiesHasMore && properties.length >= 24 && (
                <div className="text-center text-[12px] text-[#A1A1AA] py-8" data-testid="properties-end-of-list">
                  You've reached the end of the list.
                </div>
              )}
            </>
          )}
        </main>
      </div>
    ),

    success_stories: (!presentationMode && showSuccess && success.length > 0) ? (
      <section key="success_stories" className="mt-14 relative" data-testid="success-stories-section">
        {isAdmin && <AdminOrderControls sectionKey="success_stories" onMove={moveSection} />}
        {isAdmin && (
          <Link to="/admin?tab=content" className="absolute -top-2 right-0 text-[10px] font-semibold text-[#0055FF] hover:underline inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded-sm border border-[#C7D9FF]" data-testid="edit-success-stories">
            <Pencil size={10} /> Edit
          </Link>
        )}
        <div className="flex items-end justify-between mb-5 pb-3 border-b border-[#E4E4E7]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#15803D] mb-1 flex items-center gap-1.5">
              <Trophy size={12} /> Trust · Recent Closures
            </div>
            <h2 className="font-heading font-semibold text-2xl tracking-tight text-[#09090B]">
              Closed deals in the last 15 days
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {success.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      </section>
    ) : null,

    projects: (!presentationMode && showProjectsCta) ? (
      <section key="projects" className="mt-14 relative" data-testid="projects-cta-section">
        {isAdmin && <AdminOrderControls sectionKey="projects" onMove={moveSection} />}
        <Link to="/projects" className="block bg-gradient-to-br from-[#0055FF] to-[#003ECC] text-white border border-[#0044CC] rounded-sm p-6 lg:p-8 hover:shadow-lg transition-shadow group" data-testid="projects-cta">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/70 mb-2 inline-flex items-center gap-1.5">
                <Building2 size={12} /> Builder / Owner Projects · New Construction
              </div>
              <h3 className="font-heading font-bold text-3xl tracking-tight">Verified projects, multiple unit options.</h3>
              <p className="text-[13px] text-white/80 mt-2 max-w-xl">Under-construction & ready-to-move projects from approved builders — curated by House Entry.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-white text-[#0055FF] font-semibold text-[13px] px-4 py-2 rounded-sm group-hover:translate-x-1 transition-transform">
              Browse Projects <ArrowRight size={14} />
            </span>
          </div>
        </Link>
      </section>
    ) : null,

    testimonials: (!presentationMode && showTestimonials) ? (
      <div key="testimonials" className="relative mt-4">
        {isAdmin && <AdminOrderControls sectionKey="testimonials" onMove={moveSection} />}
        <TestimonialsSection />
      </div>
    ) : null,
  };

  return order.map((k) => sectionsMap[k]).filter(Boolean);
}

const Stat = ({ label, value, accent }) => (
  <div className={`border ${accent ? "border-[#0055FF] bg-[#EEF4FF]" : "border-[#E4E4E7] bg-white"} p-3 rounded-sm`} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="text-[9px] uppercase tracking-wider font-semibold text-[#52525B]">{label}</div>
    <div className={`font-heading font-bold text-xl font-mono-data ${accent ? "text-[#0055FF]" : "text-[#09090B]"}`}>{value}</div>
  </div>
);

// v2.71.59 — Compact premium upgrade card. Replaces the giant p-10 dashed
// locked panel with a slim, low-friction premium teaser.
const LockedSection = ({ title, desc, cta }) => (
  <div
    className="bg-gradient-to-r from-amber-50 via-white to-amber-50/60 border border-amber-200/60 rounded-2xl px-5 py-4 flex items-center gap-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
    data-testid="locked-top-picks"
  >
    <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700">
      <Crown size={18} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13.5px] font-semibold text-slate-900 truncate">{title}</div>
      <div className="text-[11.5px] text-slate-600 truncate">{desc}</div>
    </div>
    <Link to="/pricing" className="shrink-0">
      <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700 text-white rounded-full px-4 text-[12.5px] font-semibold">
        {cta}
      </Button>
    </Link>
  </div>
);

const avg = (arr, key) => {
  if (!arr.length) return 0;
  return arr.reduce((s, x) => s + (x[key] || 0), 0) / arr.length;
};

